import { useRef } from 'react';
import { Camera, Check, FileText, Notebook, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { askApplies } from '../api/eventRoster';
import type {
  RosterCell,
  RosterColumn,
  RosterPerson,
} from '../api/eventRoster';
import type { RsvpResponse } from '../api/events';

/**
 * A person per row, a column per thing they owe — TROOP-137.
 *
 * **Extracted so there is exactly one of it.** The trip summary and the event page's own Going and
 * Forms panels are the same table, not two tables that resemble each other: the sticky name column,
 * the going dropdown, the ask checkbox and the three tier marks are one set of components rendered
 * from both places. A leader who learns to tick down a column on the summary already knows how the
 * event page works, and the two cannot drift apart in a later commit because there is nothing to
 * drift from.
 *
 * That is also the repo's standing rule applied to a screen instead of to a policy: a second
 * implementation of "how a person answers for an ask" is a second answer to what the control means.
 *
 * **The table is presentational and asks nothing of the server.** Every cell reports upward and the
 * caller decides which route it is — `/units/` from the summary and from the docket panels, and
 * nothing at all from `/family`, which has no Everyone tab because a guardian is not an adult
 * officer of the unit (rule 15a).
 *
 * **`paper` is off by default.** The print-only twins exist for the summary sheet, and their base
 * `display: none` lives in that screen's own `PrintStyles`; rendering them anywhere else would put
 * a stray glyph beside every control on the event page. So a caller that does not print says
 * nothing and gets nothing.
 */
export function PeopleGrid({
  people,
  columns,
  going = false,
  contacts = false,
  paper = false,
  busy,
  onGoing,
  onComplete,
  onWithdraw,
  onReturnForm,
}: {
  people: RosterPerson[];
  /** One column per ask. Empty renders a table of names and nothing else, which is legitimate. */
  columns: RosterColumn[];
  /** Whether the Going column is drawn. The Forms panel wants the asks and not the answer. */
  going?: boolean;
  contacts?: boolean;
  /** Renders the print-only twins. The trip summary only — see the component remarks. */
  paper?: boolean;
  busy: boolean;
  onGoing?: (person: RosterPerson, response: RsvpResponse) => void;
  /** Ticked, or a leader recording the paper they are holding. */
  onComplete: (column: RosterColumn, person: RosterPerson, tier?: 'PhysicalCopy') => void;
  onWithdraw: (column: RosterColumn, person: RosterPerson, cell: RosterCell) => void;
  onReturnForm: (column: RosterColumn, person: RosterPerson, file: File) => void;
}) {
  return (
    // The table scrolls inside its own container. A column per ask gets wide quickly, and the page
    // body must never scroll sideways.
    <div className="pg-scroll" style={{ overflowX: 'auto' }}>
      <table
        className="t-body pg-table"
        style={{
          borderCollapse: 'collapse',
          minWidth: '100%',
          color: 'var(--color-text-primary)',
        }}
      >
        <thead>
          <tr>
            <Th sticky>Name</Th>
            {going && <Th>Going</Th>}
            {contacts && <Th>Contact</Th>}
            {columns.map((column) => (
              <Th key={column.requirementId}>
                {column.title}
                {!column.isRequired && (
                  <span className="t-caption" style={{ color: 'var(--color-text-muted)' }}>
                    {' '}
                    (optional)
                  </span>
                )}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {people.map((person) => (
            <tr key={person.personId} style={{ borderTop: '1px solid var(--color-border-subtle)' }}>
              <Td sticky>
                <span className="t-body-strong">{person.displayName}</span>
              </Td>

              {going && (
                <Td>
                  <GoingCell
                    person={person}
                    busy={busy}
                    paper={paper}
                    onChange={(response) => onGoing?.(person, response)}
                  />
                </Td>
              )}

              {contacts && (
                <Td>
                  <ContactCell person={person} />
                </Td>
              )}

              {columns.map((column) => (
                <Td key={column.requirementId}>
                  <AskCell
                    column={column}
                    person={person}
                    cell={person.cells[column.requirementId]}
                    busy={busy}
                    paper={paper}
                    onComplete={(tier) => onComplete(column, person, tier)}
                    onWithdraw={(cell) => onWithdraw(column, person, cell)}
                    onReturnForm={(file) => onReturnForm(column, person, file)}
                  />
                </Td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * How to reach this person.
 *
 * **An adult's own details; a young person's parents'.** Not a rendering choice — the server sends
 * no contact row for a young person at all, because rule 10 keeps a leader from reaching a Scout
 * off-system. The parents are the answer, and saying so on the row stops a leader hunting for
 * something that was never withheld so much as never collected.
 */
export function ContactCell({ person }: { person: RosterPerson }) {
  if (person.contact) {
    const { email, phone } = person.contact;

    if (!email && !phone) {
      return (
        <span className="t-caption" style={{ color: 'var(--color-text-muted)' }}>
          Nothing recorded
        </span>
      );
    }

    return (
      <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
        {phone}
        {phone && email && <br />}
        {email}
      </span>
    );
  }

  if (person.guardians.length === 0) {
    return (
      <span className="t-caption" style={{ color: 'var(--color-text-muted)' }}>
        No parent recorded
      </span>
    );
  }

  return (
    <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
      {person.guardians.map((guardian) => (
        <span key={guardian.personId} style={{ display: 'block' }}>
          {guardian.displayName}
          {guardian.phone && ` · ${guardian.phone}`}
          {guardian.email && ` · ${guardian.email}`}
        </span>
      ))}
    </span>
  );
}

/**
 * Going, not going, maybe, or nothing yet.
 *
 * A bare `<select>` rather than `SelectField`: that shell puts a caption above its control, which
 * is right for a form and wrong for a cell whose column heading is already the label. It carries an
 * `aria-label` so the control still says which row it belongs to when read out of context — and a
 * print-only twin, because paper wants the answer rather than the control.
 *
 * **Silence is its own option and never means "not going"** — folding the two together is how a
 * seat count comes out short.
 */
export function GoingCell({
  person,
  busy,
  paper = false,
  onChange,
}: {
  person: RosterPerson;
  busy: boolean;
  paper?: boolean;
  onChange: (response: RsvpResponse) => void;
}) {
  return (
    <>
      <select
        className={paper ? 'pg-screen-only' : undefined}
        aria-label={`Is ${person.displayName} going?`}
        value={person.rsvpResponse ?? ''}
        disabled={busy}
        onChange={(e) => {
          if (e.target.value) onChange(e.target.value as RsvpResponse);
        }}
        style={{
          font: 'inherit',
          color: 'var(--color-text-primary)',
          background: 'var(--color-surface-raised)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: 'var(--radius-sm)',
          padding: 'var(--space-1) var(--space-2)',
          minHeight: 'var(--tap-min)',
        }}
      >
        {/* Not selectable back to: a leader can change an answer but cannot un-ask it, and the
            RSVP route has no "no response" to send -- it is the absence of a row. */}
        <option value="" disabled>
          Not said
        </option>
        <option value="Going">Going</option>
        <option value="Maybe">Maybe</option>
        <option value="NotGoing">Not going</option>
      </select>

      {paper && (
        <span className="pg-print-only">
          {person.rsvpResponse === 'NotGoing' ? 'Not going' : (person.rsvpResponse ?? 'Not said')}
        </span>
      )}
    </>
  );
}

/**
 * One person against one ask.
 *
 * **A box, ticked or not.** The grid's job is to be read down a column at arm's length, and it used
 * to answer in sentences: a completed ask rendered a green tick beside "Said done", while an
 * *outstanding* one rendered its control -- a tick icon beside the word "Paid". So the cell that
 * meant *this family still owes twenty pounds* was the one wearing a tick and the word Paid, and a
 * leader working down the column read the fee list exactly backwards. A checkbox cannot do that:
 * checked is done, unchecked is not, and the same control undoes it.
 *
 * **A `Document` is the exception, because it has three answers and a box has two.** A scan, a
 * leader's binder and a family's word are separate tiers that rule 15 forbids merging, so that
 * column keeps two controls going in and shows *which* tier came back rather than that something
 * did. The word goes; the distinction stays, as an icon.
 *
 * Unticking withdraws the completion. That is the same act the small x always did, and this route
 * is adult-officers-only -- which is what rule 18 asks for in both directions, since unmarking a
 * payment is a leader's call too.
 */
export function AskCell({
  column,
  person,
  cell,
  busy,
  paper = false,
  onComplete,
  onWithdraw,
  onReturnForm,
}: {
  column: RosterColumn;
  person: RosterPerson;
  cell: RosterCell | undefined;
  busy: boolean;
  paper?: boolean;
  onComplete: (tier?: 'PhysicalCopy') => void;
  onWithdraw: (cell: RosterCell) => void;
  onReturnForm: (file: File) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);

  // Greyed, not blank -- and never an empty box. An unticked checkbox reads as "not done yet",
  // which is exactly the wrong thing to say about somebody who was never asked.
  if (!askApplies(column, person.classification)) {
    return (
      <span
        className="t-caption"
        style={{ color: 'var(--color-text-muted)' }}
        title="Not asked of them"
      >
        &mdash;
      </span>
    );
  }

  if (column.kind === 'Document') {
    return cell ? (
      <span className="inline-flex items-center" style={{ gap: 'var(--space-1)' }}>
        <TierMark tier={cell.evidenceTier} person={person} paper={paper} />
        <button
          type="button"
          className={paper ? 'pg-screen-only' : undefined}
          onClick={() => onWithdraw(cell)}
          disabled={busy}
          aria-label={`Undo ${column.title} for ${person.displayName}`}
          style={{
            background: 'none',
            border: 'none',
            padding: 2,
            cursor: busy ? 'default' : 'pointer',
            color: 'var(--color-text-muted)',
          }}
        >
          <X size={13} aria-hidden />
        </button>
      </span>
    ) : (
      <>
        {/* Paper wants the state, not the controls. An empty box is what a leader ticks by hand on
            the trip, which is the whole reason they printed it. */}
        {paper && <span className="pg-print-only">&#9744;</span>}

        <span
          className={`inline-flex items-center${paper ? ' pg-screen-only' : ''}`}
          style={{ gap: 'var(--space-1)' }}
        >
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            // No `capture`. It used to say `environment`, which on a phone opens the camera and
            // offers no way back to the camera roll -- so a parent who had already photographed
            // the slip at the kitchen table, or been sent a scan, had to photograph it again off
            // the screen. Without the attribute iOS and Android both offer the library *and* the
            // camera, which is the superset.
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onReturnForm(file);
              e.target.value = '';
            }}
          />

          <CellButton
            label={`Add the ${column.title} for ${person.displayName}`}
            busy={busy}
            onClick={() => fileInput.current?.click()}
          >
            <Camera size={14} aria-hidden />
            Photo
          </CellButton>

          {/* The other answer, and the reason this cell is not a checkbox. Half of these arrive
              as paper into a binder, and photographing seventeen pages at a Monday meeting is
              not something anybody is going to do. It is its own tier, never the scan's. */}
          <CellButton
            label={`Record the paper ${column.title} you hold for ${person.displayName}`}
            busy={busy}
            onClick={() => onComplete('PhysicalCopy')}
          >
            <Notebook size={14} aria-hidden />
            Paper
          </CellButton>
        </span>
      </>
    );
  }

  // Everything else -- a payment, a link, an acknowledgement -- has one answer, so it gets one box.
  return (
    <>
      {/*
        The printed twin. A native checkbox is a form control the print stylesheet has just forced
        to black, and browsers vary on whether its tick survives that at all; a glyph is text and
        prints like the rest of the table. The empty box is still the one a leader ticks by hand.
      */}
      {paper && <span className="pg-print-only">{cell ? <>&#9745;</> : <>&#9744;</>}</span>}

      <span
        className={`inline-flex items-center justify-center${paper ? ' pg-screen-only' : ''}`}
        style={{ minHeight: 'var(--tap-min)' }}
      >
        <input
          type="checkbox"
          checked={Boolean(cell)}
          disabled={busy}
          onChange={() => (cell ? onWithdraw(cell) : onComplete())}
          // The column heading labels this for somebody who can see the table. Read out of that
          // context the control has to say both halves itself.
          aria-label={`${column.title} — ${person.displayName}`}
          style={{
            width: '1.15rem',
            height: '1.15rem',
            accentColor: 'var(--color-status-compliant)',
            cursor: busy ? 'default' : 'pointer',
          }}
        />
      </span>
    </>
  );
}

/**
 * Which kind of proof came back for a form -- as an icon on screen, and as a word on paper.
 *
 * The three tiers never merge (rule 15), and in a grid that has to survive being read at arm's
 * length they say so in shape rather than in a sentence. The name is still on the element for
 * anybody hovering it or listening to it, which is where a screen reader gets what the column
 * heading cannot give.
 *
 * **Paper has neither of those affordances**, so the word comes back there. Nobody hovers a
 * printout, and three similar line icons down a column is exactly the collapse rule 15 forbids --
 * a leader has to be able to tell a scan from their own binder without the screen to ask.
 */
export function TierMark({
  tier,
  person,
  paper = false,
}: {
  tier: string;
  person: RosterPerson;
  paper?: boolean;
}) {
  const [Icon, name] =
    tier === 'Artifact'
      ? ([FileText, 'Scan returned'] as const)
      : tier === 'PhysicalCopy'
        ? ([Notebook, 'On paper with a leader'] as const)
        : ([Check, 'Confirmed'] as const);

  return (
    <>
      {paper && <span className="pg-print-only t-caption">{name}</span>}

      <span
        role="img"
        aria-label={`${name} — ${person.displayName}`}
        title={name}
        className={`inline-flex items-center${paper ? ' pg-screen-only' : ''}`}
        style={{ color: 'var(--color-status-compliant)' }}
      >
        <Icon size={15} aria-hidden />
      </span>
    </>
  );
}

/**
 * A small control for a table cell.
 *
 * Not `Button`: that component's smallest size is built for a form and puts three of them across a
 * phone, and its `disabledReason` contract exists so a disabled control explains itself. Here
 * "busy" is a half-second and the explanation would be noise, so the control stays enabled-looking
 * and the click is swallowed instead. The accessible name comes from `label`, because the visible
 * text ("Photo") does not say who it is for.
 */
export function CellButton({
  label,
  busy,
  onClick,
  children,
}: {
  label: string;
  busy: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={busy}
      onClick={onClick}
      className="t-caption inline-flex items-center"
      style={{
        gap: '0.25rem',
        background: 'var(--color-surface-raised)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: 'var(--radius-sm)',
        color: 'var(--color-text-primary)',
        padding: 'var(--space-1) var(--space-2)',
        minHeight: 'var(--tap-min)',
        cursor: busy ? 'default' : 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

export function Th({ children, sticky }: { children: ReactNode; sticky?: boolean }) {
  return (
    <th
      scope="col"
      className="t-caption"
      style={{
        textAlign: 'left',
        color: 'var(--color-text-secondary)',
        padding: 'var(--space-2)',
        whiteSpace: 'nowrap',
        // The name column stays put while the asks scroll past it. Without this a leader
        // scrolling right loses track of which row they are ticking.
        position: sticky ? 'sticky' : undefined,
        left: sticky ? 0 : undefined,
        // `--color-surface-page`, not `--color-surface` -- there is no such token, so the
        // declaration was dropped whole and the sticky column was transparent, letting the ask
        // columns slide visibly underneath the names. The failure mode CLAUDE.md warns about:
        // silent, invisible to tsc, and it reads as a rendering quirk rather than a typo.
        background: sticky ? 'var(--color-surface-page)' : undefined,
      }}
    >
      {children}
    </th>
  );
}

export function Td({ children, sticky }: { children: ReactNode; sticky?: boolean }) {
  return (
    <td
      style={{
        padding: 'var(--space-2)',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
        position: sticky ? 'sticky' : undefined,
        left: sticky ? 0 : undefined,
        background: sticky ? 'var(--color-surface-page)' : undefined,
      }}
    >
      {children}
    </td>
  );
}
